import { useState, useEffect, useMemo } from 'react';
import { useCompanies } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import type { CompanyBillingData, CompanySubscriptionTier } from '@/types/billing';

export function useAllCompanyBilling() {
  const { companies, isCompanyReady } = useCompanies();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionTiers, setSubscriptionTiers] = useState<CompanySubscriptionTier[]>([]);
  const [companiesBillingData, setCompaniesBillingData] = useState<CompanyBillingData[]>([]);

  // Stable dependency: only changes when company IDs actually change
  const companyIds = useMemo(
    () => companies?.map(c => c.id).sort().join(',') || '',
    [companies]
  );

  useEffect(() => {
    // Only start loading when companies are ready to avoid long authentication waits
    if (isCompanyReady && companyIds) {
      console.debug('[Billing] Companies ready, loading billing data', { 
        companiesCount: companies?.length, 
        companyIds,
        timestamp: Date.now()
      });
      loadAllBillingData();
    } else {
      console.debug('[Billing] Waiting for companies to be ready', { 
        isCompanyReady, 
        companiesCount: companies?.length,
        companyIds 
      });
    }
  }, [companyIds, isCompanyReady]);

  const loadAllBillingData = async () => {
    if (!isCompanyReady) {
      setLoading(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Load subscription tiers first
      const { data: tiers, error: tiersError } = await supabase
        .from('company_subscription_tiers')
        .select('*')
        .eq('is_active', true as any)
        .order('monthly_price');

      if (tiersError) throw tiersError;
      
      setSubscriptionTiers((tiers || []) as any);

      // Load billing data for all companies
      if (companies && companies.length > 0) {
        console.debug('[Billing] Loading billing data for companies', { count: companies.length });
        const billingDataPromises = companies.map(company => 
          loadCompanyBillingData(company.id, company.name, company.logo_url, (tiers || []) as any)
        );
        const billingResults = await Promise.all(billingDataPromises);
        const validResults = billingResults.filter(Boolean) as CompanyBillingData[];
        console.debug('[Billing] Loaded company billing results', { total: billingResults.length, valid: validResults.length });
        setCompaniesBillingData(validResults);
      } else {
        console.debug('[Billing] No companies available to load billing data', { companies });
        setCompaniesBillingData([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load billing data';
      setError(errorMessage);
      console.error('Error loading all company billing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyBillingData = async (
    companyId: string, 
    companyName: string, 
    companyLogoUrl: string | undefined,
    tiers: CompanySubscriptionTier[]
  ): Promise<CompanyBillingData | null> => {
    try {
      // Load company subscription
      const { data: subscription, error: subError } = await supabase
        .from('company_subscriptions')
        .select('*')
        .eq('company_id', companyId as any)
        .maybeSingle();

      if (subError) throw subError;

      // Load company usage using the new UUID-specific function to avoid overloading issues
      const { data: usage, error: usageError } = await supabase
        .rpc('get_current_company_usage_uuid', { company_id_param: companyId });

      if (usageError) {
        console.warn(`[Billing] Usage error for company ${companyName}:`, usageError);
        // Don't throw - allow company to be loaded without usage data
      }
      
      const companyUsage = usage && Array.isArray(usage) && usage.length > 0 ? usage[0] : null;
      
      // VALIDATION: Check credit math consistency (credits_used now positive from database)
      if (companyUsage) {
        const available = (companyUsage as any).available_credits || 0;
        const acquired = (companyUsage as any).credits_acquired || 0;
        const used = (companyUsage as any).credits_used || 0;
        const expectedAvailable = acquired - used;
        const difference = Math.abs(available - expectedAvailable);
        
        if (difference > 1) {
          console.warn('⚠️ [Credit Validation] Credit math inconsistency:', {
            companyId,
            available,
            acquired,
            used,
            expected: expectedAvailable,
            difference,
            raw_usage: usage[0]
          });
        }
      }
      
      console.debug('[Billing] Company usage loaded', { companyId, hasUsage: !!companyUsage, usage: companyUsage });

      // Find current tier (default to free if no subscription)
      const tierKey = (subscription as any)?.tier_key || 'free';
      const tier = tiers.find(t => t.tier_key === tierKey) || null;

      return {
        company: {
          id: companyId,
          name: companyName,
          logo_url: companyLogoUrl
        },
        subscription: (subscription || null) as any,
        usage: companyUsage as any,
        tier
      };

    } catch (error) {
      console.error('Error loading company billing data for', companyName, ':', error);
      return null;
    }
  };

  return {
    loading: loading || !isCompanyReady, // Show loading until companies are ready
    error,
    subscriptionTiers,
    companiesBillingData,
    refetch: loadAllBillingData
  };
}